import * as yup from 'yup';

import { CONTENT_WIDGETS, type FieldDef, type FormFieldValue } from '../types';

import completionIssue from './completionIssue';

interface BuildYupSchemaOptions {
  // Hold answers to the form's required and pattern rules. Off while a task is
  // still being worked, so partial answers can be saved; on when it's marked
  // Done. Shape rules (types, option lists) always apply.
  enforceCompletion?: boolean;
}

const autocompleteValue = yup.object({
  id: yup.string().required(),
  label: yup.string()
});

// A yup object casts a missing value to {} and then demands its `id`, which
// would make an unanswered single autocomplete invalid even in a draft. Check
// the shape only when there is a value.
const singleAutocompleteValue = (field: FieldDef) =>
  yup
    .mixed()
    .nullable()
    .test(
      'autocomplete',
      `${field.label} must be a selection`,
      (value) => value == null || autocompleteValue.isValidSync(value)
    );

const shapeSchema = (field: FieldDef): yup.Schema => {
  switch (field.widget) {
    case 'text':
    case 'textarea':
    case 'date':
      return yup.string().nullable();
    case 'number':
      return yup
        .number()
        .nullable()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError(`${field.label} must be a number`);
    case 'checkbox':
      return yup.boolean().nullable();
    case 'select': {
      const values = (field.options ?? []).map((option) => option.value);
      const single = field.allowOther ? yup.string() : yup.string().oneOf(values);
      return field.multiple ? yup.array().of(single) : single.nullable();
    }
    case 'radio': {
      const values = (field.options ?? []).map((option) => option.value);
      const single = field.allowOther ? yup.string() : yup.string().oneOf(values);
      return single.nullable();
    }
    case 'autocomplete':
      return field.multiple ? yup.array().of(autocompleteValue) : singleAutocompleteValue(field);
    case 'html_content':
    case 'image':
    case 'section_header':
      // Content widgets carry no answer; buildYupSchema filters them out
      // before calling shapeSchema, so this branch is unreachable.
      return yup.mixed();
  }
};

const fieldSchema = (field: FieldDef, enforceCompletion: boolean): yup.Schema => {
  const shape = shapeSchema(field);
  if (!enforceCompletion) return shape;
  return shape.test('completion', '', (value, context) => {
    const issue = completionIssue(field, value as FormFieldValue);
    return issue ? context.createError({ message: issue.message }) : true;
  });
};

// Clearing a date, select or autocomplete leaves null, which the backend's
// schema rejects for those types. A cleared answer is simply unanswered, so
// drop the key; completion rules still see it as blank.
const omitCleared = (value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).filter(([, answer]) => answer !== null))
    : value;

const buildYupSchema = (
  fields: FieldDef[],
  { enforceCompletion = true }: BuildYupSchemaOptions = {}
) => {
  // Content widgets carry no answer, so they have no entry in form_data.
  const answerFields = fields.filter((field) => !CONTENT_WIDGETS.includes(field.widget));
  return yup
    .object(
      Object.fromEntries(
        answerFields.map((field) => [field.key, fieldSchema(field, enforceCompletion)])
      )
    )
    .transform(omitCleared);
};

export default buildYupSchema;
